import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { ALL_CHARACTERS } from "../data/avatarsProfil";
import { useAuth } from "./AuthContext";
import { apiFetch } from "../api/client";

interface PlayerContextType {
  playerName: string;
  playerAvatar: any;
  setPlayerName: (name: string) => void;
  setPlayerAvatar: (avatar: any) => void;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

const PLAYER_NAME_KEY = "playerName";
const PLAYER_AVATAR_KEY = "playerAvatarIndex";

// L'avatar est un require() (réf. non sérialisable) : on le manipule via le NOM
// du personnage (stable, stocké en base dans student.avatarkey).
const avatarByName = (name?: string | null): any =>
  (name ? ALL_CHARACTERS.find((c) => c.name === name)?.avatar : null) ?? null;
const nameByAvatar = (avatar: any): string | null =>
  ALL_CHARACTERS.find((c) => c.avatar === avatar)?.name ?? null;

export function PlayerProvider({ children }: { children: ReactNode }) {
  const { user, getAccessToken } = useAuth();
  const [playerName, setPlayerNameState] = useState<string>("Joueur");
  const [playerAvatar, setPlayerAvatarState] = useState<any>(null);

  // 1) Repli LOCAL (invité / hors-ligne) : restaure nom + avatar (par index).
  useEffect(() => {
    AsyncStorage.getItem(PLAYER_NAME_KEY).then((val) => {
      if (val) setPlayerNameState(val);
    });
    AsyncStorage.getItem(PLAYER_AVATAR_KEY).then((val) => {
      if (val == null) return;
      const idx = parseInt(val, 10);
      if (Number.isInteger(idx) && idx >= 0 && idx < ALL_CHARACTERS.length) {
        setPlayerAvatarState(ALL_CHARACTERS[idx].avatar);
      }
    });
  }, []);

  // 2) Source de vérité = la BASE quand connecté : GET /me/profile.
  useEffect(() => {
    const token = getAccessToken();
    if (!user || !token) return;
    apiFetch<{ displayName?: string; avatarName?: string }>("/me/profile", { token, auth: true })
      .then((p) => {
        if (p?.displayName) setPlayerNameState(p.displayName);
        const a = avatarByName(p?.avatarName);
        if (a) setPlayerAvatarState(a);
      })
      .catch(() => { /* offline / service indispo -> on garde le local */ });
  }, [user, getAccessToken]);

  const persistProfile = (patch: { displayName?: string; avatarName?: string }) => {
    const token = getAccessToken();
    if (!user || !token) return;
    apiFetch("/me/profile", { method: "PUT", body: patch, token, auth: true }).catch(() => {});
  };

  const setPlayerName = (name: string) => {
    setPlayerNameState(name);
    AsyncStorage.setItem(PLAYER_NAME_KEY, name);   // cache local
    persistProfile({ displayName: name });          // base (si connecté)
  };

  const setPlayerAvatar = (avatar: any) => {
    setPlayerAvatarState(avatar);
    const idx = ALL_CHARACTERS.findIndex((c) => c.avatar === avatar);
    if (idx >= 0) AsyncStorage.setItem(PLAYER_AVATAR_KEY, String(idx));
    else AsyncStorage.removeItem(PLAYER_AVATAR_KEY);
    const name = nameByAvatar(avatar);
    if (name) persistProfile({ avatarName: name });
  };

  return (
    <PlayerContext.Provider value={{ playerName, playerAvatar, setPlayerName, setPlayerAvatar }}>
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer must be used within a PlayerProvider");
  return ctx;
}
